import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { Users } from "../types/Types";
import { setPaymentLogUserContext } from "../utils/paymentErrorLogger";
import { clearExplorerSessionOnLogout } from "../explorer/explorerSession";

const STORAGE_KEY_OBJECT_ID = "objectId";
const STORAGE_KEY_VIEW_MODE = "smbf_view_mode";

export type ViewMode = "member" | "admin";

type AppContextType = {
  objectId: string | null;
  setObjectId: (id: string | null) => void;
  user: Users | undefined;
  setUser: React.Dispatch<React.SetStateAction<Users | undefined>>;
  sessionExpired: boolean;
  setSessionExpired: (expired: boolean) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);
AppContext.displayName = "AppContext";

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [objectId, setObjectId] = useState<string | null>(null);
  const [user, setUser] = useState<Users | undefined>(undefined);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_VIEW_MODE) === "admin"
        ? "admin"
        : "member";
    } catch {
      return "member";
    }
  });
  const prevObjectIdRef = useRef<string | null>(null);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY_VIEW_MODE, mode);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  // Hydrate objectId from storage on mount only
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY_OBJECT_ID);
    if (stored) setObjectId(stored);
  }, []);

  // Persist objectId when it changes (setters are stable, no need in deps)
  useEffect(() => {
    if (objectId !== prevObjectIdRef.current) {
      if (prevObjectIdRef.current != null) {
        setUser(undefined);
      }
      prevObjectIdRef.current = objectId;
    }
    if (objectId) {
      localStorage.setItem(STORAGE_KEY_OBJECT_ID, objectId);
    } else {
      localStorage.removeItem(STORAGE_KEY_OBJECT_ID);
    }
  }, [objectId]);

  /** Same-tab logout clears tokens before this runs; other tabs rely on `storage` (no CustomEvent across tabs). */
  useEffect(() => {
    const resetClientAfterLogout = () => {
      setObjectId(null);
      setUser(undefined);
      setViewModeState("member");
      try {
        localStorage.removeItem(STORAGE_KEY_VIEW_MODE);
      } catch {
        /* ignore storage errors */
      }
    };
    const onSmbfLogout = () => resetClientAfterLogout();
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "access_token") return;
      if (e.newValue != null) return;
      if (e.oldValue == null) return;
      clearExplorerSessionOnLogout();
      resetClientAfterLogout();
    };
    window.addEventListener("smbf-logout", onSmbfLogout);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("smbf-logout", onSmbfLogout);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Sync current login user to payment error logger so ClientError logs save with user id/email/name (no N/A)
  useEffect(() => {
    const uid = objectId ?? user?.user?.objectId;
    const email = user?.user?.email;
    const name =
      user?.user?.firstName != null && user?.user?.lastName != null
        ? `${user.user.firstName} ${user.user.lastName}`.trim()
        : undefined;
    setPaymentLogUserContext({
      userId: uid ?? undefined,
      userEmail: email ?? undefined,
      userName: name || undefined,
    });
  }, [objectId, user]);

  // Non-admins cannot stay in admin view mode
  useEffect(() => {
    if (user && user.user?.isAdmin !== 1 && viewMode === "admin") {
      setViewMode("member");
    }
  }, [user, viewMode, setViewMode]);

  const value = useMemo<AppContextType>(
    () => ({
      objectId,
      setObjectId,
      user,
      setUser,
      sessionExpired,
      setSessionExpired,
      viewMode,
      setViewMode,
    }),
    [objectId, user, sessionExpired, viewMode, setViewMode],
  );

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
